import { exists } from "../../utils/generic";
import { RowNode } from "../../entities/rowNode";
import { pushAll } from "../../utils/array";
import { GridOptionsWrapper } from "../../gridOptionsWrapper";
import { Autowired, Bean } from "../../context/context";
import { StylingService } from "../../styling/stylingService";
import { RowClassParams } from "../../entities/gridOptions";
export interface RowCssClassCalculatorParams {
  rowNode: RowNode;
  rowIsEven: boolean;
  rowLevel: number;
  fullWidthRow?: boolean;
  firstRowOnPage: boolean;
  lastRowOnPage: boolean;
  printLayout: boolean;
  expandable: boolean;

  extraCssClass?: string;
  rowFocused?: boolean;
  fadeRowIn?: boolean;
  scope?: any;
}

/**
 * Common logic for RowComp and AutoHeightCalculator
 */
@Bean('rowCssClassCalculator')
export class RowCssClassCalculator {

  @Autowired('stylingService') public stylingService: StylingService;
  @Autowired('gridOptionsWrapper') public gridOptionsWrapper: GridOptionsWrapper;

  public getInitialRowClasses(params: RowCssClassCalculatorParams): string[] {

    const classes: string[] = [];

    if (exists(params.extraCssClass)) {
      classes.push(params.extraCssClass);
    }

    classes.push('dy-row');
    classes.push(params.rowFocused ? 'dy-row-focus' : 'dy-row-no-focus');

    if (params.fadeRowIn) {
      classes.push('dy-opacity-zero');
    }

    classes.push(params.rowIsEven ? 'dy-row-even' : 'dy-row-odd');

    if (params.rowNode.isRowPinned()) {
      classes.push('dy-row-pinned');
    }

    if (params.rowNode.isSelected()) {
      classes.push('dy-row-selected');
    }

    if (params.rowNode.footer) {
      classes.push('dy-row-footer');
    }

    classes.push('dy-row-level-' + params.rowLevel);

    if (params.rowNode.stub) {
      classes.push('dy-row-loading');
    }

    if (params.fullWidthRow) {
      classes.push('dy-full-width-row');
    }

    if (params.expandable) {
      classes.push('dy-row-group');
      classes.push(params.rowNode.expanded ? 'dy-row-group-expanded' : 'dy-row-group-contracted');
    }

    if (params.rowNode.dragging) {
      classes.push('dy-row-dragging');
    }

    pushAll(classes, this.processClassesFromGridOptions(params.rowNode, params.scope));
    pushAll(classes, this.preProcessRowClassRules(params.rowNode, params.scope));

    // we use absolute position unless we are doing print layout
    classes.push(params.printLayout ? 'dy-row-position-relative' : 'dy-row-position-absolute');

    if (params.firstRowOnPage) {
      classes.push('dy-row-first');
    }

    if (params.lastRowOnPage) {
      classes.push('dy-row-last');
    }

    return classes;
  }

  public processClassesFromGridOptions(rowNode: RowNode, scope: any): string[] {
    const res: string[] = [];

    const process = (rowCls: string | string[]) => {
      if (typeof rowCls === 'string') {
        res.push(rowCls);
      } else if (Array.isArray(rowCls)) {
        rowCls.forEach(e => res.push(e));
      }
    };

    // part 1 - rowClass
    const rowClass = this.gridOptionsWrapper.getRowClass();
    if (rowClass) {
      if (typeof rowClass === 'function') {
        console.warn('DYNA Grid: rowClass should not be a function, please use getRowClass instead');
        return [];
      }
      process(rowClass);
    }

    // part 2 - rowClassFunc
    const rowClassFunc = this.gridOptionsWrapper.getRowClassFunc();

    if (rowClassFunc) {
      const params: RowClassParams = {
        data: rowNode.data,
        node: rowNode,
        rowIndex: rowNode.rowIndex!,
        $scope: scope,
        api: this.gridOptionsWrapper.getApi()!,
        columnApi: this.gridOptionsWrapper.getColumnApi()!,
        context: this.gridOptionsWrapper.getContext()
      };
      const rowClassFuncResult = rowClassFunc(params);
      process(rowClassFuncResult);
    }

    return res;
  }

  private preProcessRowClassRules(rowNode: RowNode, scope: any): string[] {
    const res: string[] = [];

    this.processRowClassRules(rowNode, scope,
      (className: string) => {
        res.push(className);
      },
      (className: string) => {
        // not catered for, if creating, no need
        // to remove class as it was never there
      }
    );

    return res;
  }

  public processRowClassRules(rowNode: RowNode, scope: any, onApplicableClass: (className: string) => void, onNotApplicableClass?: (className: string) => void): void {
    const rowClassParams: RowClassParams = {
      data: rowNode.data,
      node: rowNode,
      rowIndex: rowNode.rowIndex!,
      api: this.gridOptionsWrapper.getApi()!,
      columnApi: this.gridOptionsWrapper.getColumnApi()!,
      $scope: scope,
      context: this.gridOptionsWrapper.getContext()
    };

    this.stylingService.processClassRules(
      this.gridOptionsWrapper.rowClassRules(),
      rowClassParams,
      onApplicableClass,
      onNotApplicableClass
    );
  }

  public calculateRowLevel(rowNode: RowNode): number {
    if (rowNode.group) {
      return rowNode.level;
    }

    // if a leaf, and a parent exists, put a level of the parent, else put level of 0 for top level item
    return rowNode.parent ? (rowNode.parent.level + 1) : 0;
  }

}
